/**
 * Sistema de rotas type-safe com enum único.
 *
 * Convenções:
 * - Placeholders entre colchetes: [id], [department], [slug]
 * - Paths sem prefixo de departamento (adicionado por useDepartmentNavigate)
 * - SCREAMING_SNAKE_CASE para keys do enum
 * - Paths completos (não segmentos isolados)
 *
 * @example
 * // Uso básico
 * navigate(RoutePathEnum.TALENTS)
 *
 * @example
 * // Uso com parâmetros
 * navigate(buildRoute(RoutePathEnum.TALENT_DATA, { id: "123" }))
 */

import { DepartmentSlug } from "@/types/department";

export enum RoutePathEnum {
  // ==================== ROTAS DE TALENTOS ====================

  /** Lista de talentos ativos */
  TALENTS = "/talentos",

  /** Lista de talentos arquivados */
  TALENTS_ARCHIVED = "/arquivados",

  /** Formulário de criação de talento */
  TALENT_CREATE = "/talentos/cadastrar",

  /** Dados pessoais do talento */
  TALENT_DATA = "/talentos/[id]/dados-talento",

  /** Edição de dados do talento (Casting only) */
  TALENT_DATA_EDIT = "/talentos/[id]/dados-talento/editar",

  /** Book fotográfico do talento */
  TALENT_BOOK = "/talentos/[id]/book",

  /** Composites do talento (Casting only) */
  TALENT_COMPOSITES = "/talentos/[id]/composites",

  /** Tela de Criação de Composite */
  TALENT_COMPOSITE_CREATE = "/talentos/[id]/composites/criar",

  /** Tela de Edição de Composite */
  TALENT_COMPOSITE_EDIT = "/talentos/[id]/composites/editar/[compositeId]",

  /** Lista de contratos do talento (Casting only) */
  TALENT_CONTRACTS = "/talentos/[id]/contratos",

  /** Documentos do talento (Casting only) */
  TALENT_DOCUMENTS = "/talentos/[id]/documentos",

  /** Histórico de jobs do talento (Casting only) */
  TALENT_JOB_HISTORY = "/talentos/[id]/historico-jobs",

  // ==================== ROTAS DE JOBS ====================

  /** Lista de jobs (Casting only) */
  JOBS = "/jobs",

  /** Criação de novo job (Casting only) */
  CREATE_NEW_JOB = "/jobs/novo-job",

  /** Edição de job (Casting only) */
  EDIT_JOB = "/jobs/[id]/[stageId]/editar-job",

  /** Detalhe de job (Casting only) */
  JOB_DETAIL = "/jobs/[id]/casting",

  /** Detalhe de etapa de job */
  JOB_STAGE = "/jobs/[id]/[stageName]",

  /** Lista de talentos selecionados do job na etapa Casting */
  JOB_CASTING_TALENTS = "/jobs/[id]/casting/talentos-selecionados",

  // ==================== ROTAS DE COLABORADORES ====================

  /** Lista de colaboradores */
  COLLABORATORS = "/colaboradores",

  // ==================== ROTAS DE PRODUÇÃO ====================

  /** Agenda de produção */
  SCHEDULE = "/agenda",

  /** Dados do talento atendido na agenda */
  SCHEDULE_TALENT_DATA = "/agenda/[scheduleId]/dados-talento",

  /** Produção do atendimento na agenda */
  SCHEDULE_PRODUCTION = "/agenda/[scheduleId]/producao",

  /** Contratos do atendimento na agenda */
  SCHEDULE_CONTRACTS = "/agenda/[scheduleId]/contratos",

  /** Produção artística do atendimento na agenda */
  SCHEDULE_ARTISTIC_PRODUCTION = "/agenda/[scheduleId]/producao-artistica",

  /** Fotografia do atendimento na agenda */
  SCHEDULE_PHOTOGRAPHY = "/agenda/[scheduleId]/fotografia",

  /** Lista de reprovados */
  REJECTED = "/reprovados",

  /** Dados do talento reprovado */
  REJECTED_TALENT_DATA = "/reprovados/[talentId]/dados-talento",

  // ==================== ROTAS DE CONTA ====================

  /** Configurações da conta do usuário */
  MY_ACCOUNT = "/minha-conta",

  // ==================== ROTAS PÚBLICAS ====================

  /** Dashboard principal (user.ts) */
  DASHBOARD = "/dashboard",

  /** Página de login */
  LOGIN = "/login",

  /** Página de recuperação de senha */
  FORGOT_PASSWORD_PARAM = "esqueci-minha-senha",
  NEW_PASSWORD_PARAM = "nova-senha",
  FIRST_ACCESS_PARAM = "primeiro-acesso",
  NOT_FOUND_PARAM = "404",
  EXPIRED_LINK_PARAM = "link-invalido",

  /** Página de solicitação de atualização (talent-side) */
  TALENT_REQUEST_UPDATE = "/talento/solicitar-atualizacao",
}

/**
 * Substitui placeholders em paths por valores reais.
 *
 * Placeholders suportados:
 * - [id] - ID de entidade (talento, job, etc.)
 * - [department] - Slug do departamento
 * - Qualquer outro placeholder customizado
 *
 * @param path - RoutePathEnum com placeholders
 * @param params - Objeto com valores de substituição
 * @returns Path com placeholders substituídos
 *
 * @example
 * buildRoute(RoutePathEnum.TALENT_DATA, { id: "123" })
 * // → "/talentos/123/dados-talento"
 *
 * @example
 * buildRoute(RoutePathEnum.TALENTS)
 * // → "/talentos"
 *
 * @example
 * // Uso com useDepartmentNavigate
 * const navigate = useDepartmentNavigate();
 * navigate(buildRoute(RoutePathEnum.TALENT_DATA_EDIT, { id: talentId }));
 * // → navega para /casting/talentos/123/dados-talento/editar
 */
export const buildRoute = (
  path: RoutePathEnum,
  params: Record<string, string> = {},
  department?: DepartmentSlug | null,
): string => {
  return Object.entries(params).reduce(
    (result, [key, value]) => result.replace(`[${key}]`, value),
    `${department ? `/${department}/` : ""}${path}`,
  );
};
